import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { StageBadge } from "@/components/StageBadge";
import DealDrawer from "@/components/DealDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Loader2, Plus, UserRound, Trophy, Clock } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", location: "", room_count: "", rental_expectations: "", details: "", notes: "" };

export default function Consultants() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [openC, setOpenC] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/consultants", { params: search ? { search } : {} }).then(({ data }) => setRows(data)).finally(() => setLoading(false));
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try { await api.post("/consultants", form); setAddOpen(false); setForm(EMPTY); load(); toast.success("Consultant added"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Consultants" subtitle="Property consultants and their referral performance.">
        <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56" data-testid="consultant-search" />
        <Button size="sm" onClick={() => { setForm(EMPTY); setAddOpen(true); }} data-testid="add-consultant-btn"><Plus className="h-4 w-4" /> Add</Button>
      </PageHeader>

      <div className="px-8 py-6">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0047FF]" /></div>
          : rows.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm" data-testid="consultants-empty">No consultants yet.</div>
          : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {rows.map((c) => (
                <div key={c.id} onClick={() => setOpenC(c)} className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-[#0047FF] transition-colors" data-testid={`consultant-card-${c.id}`}>
                  <div className="flex items-center gap-2"><UserRound className="h-4 w-4 text-[#0047FF]" /><span className="font-semibold text-gray-900">{c.name || "Unnamed"}</span></div>
                  <div className="text-xs text-gray-500 mt-1">{c.phone || "—"} · {c.location || "—"}</div>
                  <div className="flex gap-4 mt-3 text-xs text-gray-600">
                    <span className="flex items-center gap-1"><Trophy className="h-3.5 w-3.5 text-amber-500" />{c.win_rate != null ? `${c.win_rate}%` : "—"} win</span>
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{c.avg_cycle_days != null ? `${c.avg_cycle_days}d` : "—"} cycle</span>
                    <span>{c.won_count || 0}/{c.closed_count || 0} won</span>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="consultant-dialog">
          <DialogHeader><DialogTitle>Add consultant</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="c-name" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="c-phone" /></div>
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" data-testid="c-location" /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" data-testid="c-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form.name} data-testid="c-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConsultantDrawer consultant={openC} open={!!openC} onOpenChange={(v) => !v && setOpenC(null)} />
    </div>
  );
}

function ConsultantDrawer({ consultant, open, onOpenChange }) {
  const [deals, setDeals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [dealId, setDealId] = useState(null);
  const [dopen, setDopen] = useState(false);
  useEffect(() => {
    if (open && consultant) {
      api.get(`/consultants/${consultant.id}/deals`).then(({ data }) => setDeals(data.deals)).catch(() => setDeals([]));
      api.get(`/consultants/${consultant.id}/leads`).then(({ data }) => setLeads(data.leads)).catch(() => setLeads([]));
    }
  }, [open, consultant]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[560px] bg-white overflow-y-auto" data-testid="consultant-drawer">
        <SheetHeader><SheetTitle className="font-heading">{consultant?.name}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Mapped leads ({leads.length})</div>
          {leads.length === 0 ? <div className="text-sm text-gray-400 py-3 text-center">No leads mapped.</div> :
            leads.map((l) => (
              <div key={l.id} className="border border-gray-200 rounded-md p-2.5 text-sm flex items-center justify-between" data-testid={`c-lead-${l.id}`}>
                <span>{l.name || l.phone}<span className="text-gray-400"> · {l.city || "—"}</span></span>
                <span className="text-xs capitalize text-gray-500">{l.status}</span>
              </div>
            ))}
        </div>
        <div className="mt-6 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Referred deals ({deals.length})</div>
          {deals.length === 0 ? <div className="text-sm text-gray-400 py-6 text-center">No deals referred yet.</div> :
            deals.map((d) => (
              <div key={d.id} onClick={() => { setDealId(d.id); setDopen(true); }} className="border border-gray-200 rounded-md p-3 flex items-center justify-between cursor-pointer hover:border-[#0047FF]" data-testid={`c-deal-${d.id}`}>
                <span className="text-sm font-medium">{d.property_name}</span>
                <StageBadge stage={d.stage} />
              </div>
            ))}
        </div>
        <DealDrawer dealId={dealId} open={dopen} onOpenChange={setDopen} />
      </SheetContent>
    </Sheet>
  );
}
